import React, { memo } from 'react';
import { Alert, Box, Chip, Divider, Stack, Typography } from '@mui/material';
import type { WorkflowRun } from '../../../types/backend';

interface Props {
  run: WorkflowRun;
  /** Whether this surface offers the abandon. False on the customer's read-only history, where
   *  naming abandon as the way out would point at a button that is not there. */
  canAbandon?: boolean;
}

// What is left of a run whose pinned workflow version was deleted: its own record, and nothing
// reconstructed. The definition held the labels, the goals, the shape and the step count, so none
// of those can be shown. What the run holds itself is substantial and is what the operator came
// for: which steps it reached, what the agent recorded against each, and the timestamped acts.
//
// Deliberately not a procedure view. No diagram, no "4 of 7", no de-slugged label standing in for
// a title nobody wrote. Each of those would read as data while being a guess, and the one run
// worth reading carefully is exactly this one.
const WorkflowStrandedRun = memo<Props>(({ run, canAbandon = true }) => {
  const nodeStates = run.node_states ?? {};
  const nodeIds = Object.keys(nodeStates).sort();
  const decisions = run.decisions ?? [];
  const gates = run.gates ?? [];
  const failures = run.failures ?? [];

  return (
    <Stack spacing={2}>
      <Alert severity="warning">
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Workflow no longer available
        </Typography>
        <Typography variant="caption" sx={{ display: 'block' }}>
          Version {run.workflow_ref.version} of {run.workflow_ref.name} was deleted, so this run
          cannot continue and its steps have no names or order any more. What it recorded is below.
        </Typography>
        <Typography variant="caption" sx={{ display: 'block' }}>
          {canAbandon
            ? 'Abandoning it is the way to close it out.'
            : 'It is closed out from the investigation that holds it.'}
        </Typography>
      </Alert>

      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
        <Typography variant="caption" color="text.secondary">
          Started {run.started_at}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Last changed {run.updated_at}
        </Typography>
        {/* A stage number on its own. The total lived in the definition, and "stage 3 of 7"
            with a guessed 7 would be the one number an operator would actually rely on. */}
        <Typography variant="caption" color="text.secondary">
          Reached stage {run.current_layer}
        </Typography>
      </Stack>

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Steps, by the id the definition gave them
        </Typography>
        <Stack spacing={1}>
          {nodeIds.map((nodeId) => (
            <Box key={nodeId} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.25 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', overflowWrap: 'anywhere' }}>
                  {nodeId}
                </Typography>
                <Chip size="small" label={nodeStates[nodeId]} />
              </Stack>
              {run.evidence?.[nodeId] && (
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                  {run.evidence[nodeId]}
                </Typography>
              )}
              {Object.entries(run.facts?.[nodeId] ?? {}).map(([key, value]) => (
                <Typography key={key} variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                  {key}: {value}
                </Typography>
              ))}
            </Box>
          ))}
        </Stack>
      </Box>

      {(decisions.length > 0 || gates.length > 0 || failures.length > 0) && (
        <>
          <Divider />
          <Box>
            {/* Three logs rather than one merged chronology: only these carry their own times.
                Evidence and facts do not, so interleaving them would invent an order. */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              What was decided, approved and reported
            </Typography>
            <Stack spacing={0.75}>
              {decisions.map((d) => (
                <Typography key={`${d.node_id}-${d.decided_at}`} variant="caption">
                  {d.decided_at} · {d.node_id} took {d.chosen_condition}
                  {d.reason ? ` because ${d.reason}` : ''}
                </Typography>
              ))}
              {gates.map((g) => (
                <Typography key={`${g.node_id}-${g.requested_at}`} variant="caption">
                  {g.resolved_at ?? g.requested_at} · {g.node_id} gate{' '}
                  {g.resolution ? `${g.resolution} by ${g.resolved_by ?? 'someone'}` : 'still open'}
                </Typography>
              ))}
              {failures.map((f) => (
                <Typography key={`${f.node_id}-${f.failed_at}`} variant="caption" color="error.main">
                  {f.failed_at} · {f.node_id} failed ({f.cause}): {f.error}
                </Typography>
              ))}
            </Stack>
          </Box>
        </>
      )}
    </Stack>
  );
});

WorkflowStrandedRun.displayName = 'WorkflowStrandedRun';

export default WorkflowStrandedRun;
